import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ErrorType, type AppError } from './error-handler';
import { getConfig } from './config';

/**
 * Application Insights - Telemetria e Monitoramento de Erros
 * Integrado com GlobalErrorHandler para capturar e enviar erros ao Azure
 */

// Chaves de dados sensíveis que devem ser removidas antes do envio
const SENSITIVE_KEYS = [
  'password',
  'passwd',
  'pwd',
  'token',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'auth',
  'bearer',
  'senha',
  'carta',
  'cartao',
  'creditcard',
  'cardnumber',
  'cvv',
  'ssn',
  'secret',
  'apikey',
  'api_key',
  'privatekey',
  'private_key'
];

let client: ApplicationInsights | null = null;
const recentErrors = new Map<string, number>(); // fingerprint -> timestamp
const MAX_RECENT_ERRORS = 100;
const DEDUP_WINDOW_MS = 30000; // 30 segundos para deduplicação

/**
 * Sanitiza um objeto removendo valores de chaves sensíveis
 * Recursivo para objetos aninhados
 */
function sanitizeValue(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(sanitizeValue);
  }

  if (typeof obj === 'object') {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      const isSensitive = SENSITIVE_KEYS.some(sensitiveKey =>
        lowerKey.includes(sensitiveKey)
      );

      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeValue(value);
      }
    }
    return sanitized;
  }

  return obj;
}

/**
 * Mapeia ErrorType para SeverityLevel do Application Insights
 */
function mapSeverity(errorType: ErrorType): SeverityLevel {
  switch (errorType) {
    case ErrorType.AUTH:
    case ErrorType.PERMISSION:
    case ErrorType.NOT_FOUND:
    case ErrorType.VALIDATION:
    case ErrorType.NETWORK:
      return SeverityLevel.Warning;
    case ErrorType.API:
    case ErrorType.UNKNOWN:
    default:
      return SeverityLevel.Error;
  }
}

/**
 * Gera fingerprint do erro para deduplicação
 * Combinação de tipo e mensagem
 */
function generateFingerprint(errorType: ErrorType, message: string): string {
  // Hash simples: concatenação do tipo e mensagem (primeira 100 chars)
  const msgTruncated = message.substring(0, 100);
  return `${errorType}:${msgTruncated}`;
}

/**
 * Limpa o mapa de erros recentes se ultrapassar o limite
 */
function cleanRecentErrors(): void {
  if (recentErrors.size > MAX_RECENT_ERRORS) {
    // Remove as 10 entradas mais antigas
    const sorted = Array.from(recentErrors.entries())
      .sort((a, b) => a[1] - b[1])
      .slice(0, 10);

    sorted.forEach(([key]) => {
      recentErrors.delete(key);
    });
  }
}

/**
 * Inicializa o Application Insights com a connection string do Azure
 * Deve ser chamado no boot da aplicação (AppWrapper.tsx)
 */
export function initAppInsights(): void {
  // Não inicializa em desenvolvimento
  if (import.meta.env.DEV) {
    console.debug('[AppInsights] Telemetria desabilitada em desenvolvimento');
    return;
  }

  const connectionString = getConfig().appInsightsConnectionString;

  // Não inicializa se não houver connection string configurada
  if (!connectionString) {
    console.debug('[AppInsights] Connection string não configurada');
    return;
  }

  try {
    client = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false, // Roteamento manual via GlobalErrorHandler
        disableFetchTracking: false, // Rastrear requisições fetch
        enableResponseHeaderTracking: true,
        enableRequestHeaderTracking: true,
        enableAjaxErrorStatusText: true,
        enableUnhandledPromiseRejectionTracking: true,
        isStorageUseDisabled: false,
        // 25% (corte de custo 2026-05-27). Só PRD inicializa o AI — TST/HMG
        // recebem connectionString vazia no config.json e nem chegam aqui.
        samplingPercentage: 25,
      }
    });

    client.loadAppInsights();
    console.debug('[AppInsights] Inicializado com sucesso');
  } catch (error) {
    console.error('[AppInsights] Erro ao inicializar:', error);
    client = null;
  }
}

/**
 * Define o contexto do usuário autenticado
 * Deve ser chamado após login bem-sucedido
 */
export function setAppInsightsUser(userContext: {
  id: string;
  email?: string;
  tenantId?: string;
}): void {
  if (!client) {
    return;
  }

  try {
    // setAuthenticatedUserContext(userId, accountId, storeInCookie)
    // Envia userId e tenantId como accountId para contexto de usuário
    client.setAuthenticatedUserContext(userContext.id, userContext.tenantId, false);

    console.debug('[AppInsights] Contexto de usuário configurado:', userContext.id);
  } catch (error) {
    console.error('[AppInsights] Erro ao configurar usuário:', error);
  }
}

/**
 * Limpa o contexto do usuário autenticado
 * Deve ser chamado no logout
 */
export function clearAppInsightsUser(): void {
  if (!client) {
    return;
  }

  try {
    client.clearAuthenticatedUserContext();
    console.debug('[AppInsights] Contexto de usuário removido');
  } catch (error) {
    console.error('[AppInsights] Erro ao limpar contexto:', error);
  }
}

/**
 * Rastreia um erro no Application Insights
 * Chamado automaticamente pelo GlobalErrorHandler.reportError()
 *
 * @param appError - Erro normalizado do GlobalErrorHandler
 * @param context - Contexto adicional (URL, headers, etc.)
 */
export function trackAppError(
  appError: AppError,
  context?: Record<string, unknown>
): void {
  if (!client) {
    return;
  }

  try {
    // Deduplicação: verifica se o mesmo erro foi enviado recentemente
    const fingerprint = generateFingerprint(appError.type, appError.message);
    const lastOccurrence = recentErrors.get(fingerprint);
    const now = Date.now();

    if (lastOccurrence && now - lastOccurrence < DEDUP_WINDOW_MS) {
      console.debug('[AppInsights] Erro duplicado, ignorado:', fingerprint);
      return;
    }

    // Registra o erro neste momento
    recentErrors.set(fingerprint, now);
    cleanRecentErrors();

    // Sanitiza o contexto removendo dados sensíveis
    const sanitizedContext = context ? sanitizeValue(context) as Record<string, unknown> : {};

    // Adiciona informações do erro ao contexto
    const properties: Record<string, string> = {
      errorType: appError.type,
      message: appError.message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...(sanitizedContext ? Object.entries(sanitizedContext).reduce((acc, [key, val]) => {
        acc[key] = String(val);
        return acc;
      }, {} as Record<string, string>) : {})
    };

    // Se houver detalhes adicionais, adiciona ao contexto
    if (appError.details) {
      properties.details = JSON.stringify(sanitizeValue(appError.details));
    }

    if (appError.retryable) {
      properties.retryable = 'true';
    }

    // Determina a severidade baseada no tipo de erro
    const severity = mapSeverity(appError.type);

    // Rastreia a exception no Application Insights
    const exceptionToTrack = appError.originalError
      ? appError.originalError
      : new Error(appError.message);

    client.trackException({
      exception: exceptionToTrack,
      severityLevel: severity,
      properties
    });

    console.debug('[AppInsights] Erro rastreado:', {
      type: appError.type,
      message: appError.message,
      severity
    });
  } catch (error) {
    console.error('[AppInsights] Erro ao rastrear:', error);
  }
}

/**
 * Rastreia um evento customizado
 * Pode ser usado para métricas e performance
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (!client) {
    return;
  }

  try {
    client.trackEvent({
      name,
      properties: properties ? sanitizeValue(properties) as Record<string, string> : undefined,
      measurements
    });
  } catch (error) {
    console.error('[AppInsights] Erro ao rastrear evento:', error);
  }
}

/**
 * Rastreia uma métrica de performance
 */
export function trackMetric(name: string, value: number): void {
  if (!client) {
    return;
  }

  try {
    client.trackMetric({
      name,
      average: value
    });
  } catch (error) {
    console.error('[AppInsights] Erro ao rastrear métrica:', error);
  }
}
